import { useState, useEffect } from 'react'
import supabase from '../../lib/supabase'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'

function ServerInfo() {
  const [serverInfo, setServerInfo] = useState({
    supabaseUrl: '',
    anonKey: '',
    serviceRoleKey: '',
    recaptchaSiteKey: '',
    recaptchaSecretKey: '',
    tables: [],
    policies: [],
    functions: [],
    loading: true,
    error: null
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showKeys, setShowKeys] = useState(false)
  const [activeTab, setActiveTab] = useState('tables')
  
  useEffect(() => {
    fetchServerInfo()
  }, [])
  
  const fetchServerInfo = async () => {
    try {
      setLoading(true)
      
      // Fetch tables - Fix the catch error handling
      let tablesData = null;
      let tablesError = null;
      try {
        const { data, error } = await supabase.rpc('get_tables_info');
        tablesData = data;
        tablesError = error;
      } catch (err) {
        tablesError = { message: 'Function get_tables_info not available' };
      }
      
      if (tablesError && tablesError.message !== 'Function get_tables_info not available') {
        console.error('Error fetching tables:', tablesError);
      }
      
      // Fetch policies - Fix the catch error handling
      let policiesData = null;
      let policiesError = null;
      try {
        const { data, error } = await supabase.rpc('get_policies_info');
        policiesData = data;
        policiesError = error;
      } catch (err) {
        policiesError = { message: 'Function get_policies_info not available' };
      }
      
      if (policiesError && policiesError.message !== 'Function get_policies_info not available') {
        console.error('Error fetching policies:', policiesError);
      }
      
      // Fetch functions - Fix the catch error handling
      let functionsData = null;
      let functionsError = null;
      try {
        const { data, error } = await supabase.rpc('get_functions_info');
        functionsData = data;
        functionsError = error;
      } catch (err) {
        functionsError = { message: 'Function get_functions_info not available' };
      }
      
      if (functionsError && functionsError.message !== 'Function get_functions_info not available') {
        console.error('Error fetching functions:', functionsError);
      }
      
      // Fetch server settings
      const { data: serverSettings, error: settingsError } = await supabase
        .from('codevaro_server_settings')
        .select('settings')
        .eq('id', 'server_info')
        .maybeSingle()
      
      if (settingsError && settingsError.code !== 'PGRST116') {
        console.error('Error fetching server settings:', settingsError)
      }
      
      // Set manual table info if RPC functions are not available
      const manualTables = [
        { name: 'codevaro_services', schema: 'public', description: 'Stores service information' },
        { name: 'codevaro_categories', schema: 'public', description: 'Stores service categories' },
        { name: 'codevaro_solutions', schema: 'public', description: 'Stores solution information' },
        { name: 'codevaro_leads', schema: 'public', description: 'Stores lead information' },
        { name: 'codevaro_settings', schema: 'public', description: 'Stores application settings' },
        { name: 'codevaro_server_settings', schema: 'public', description: 'Stores server settings' },
        { name: 'codevaro_storage', schema: 'public', description: 'Tracks uploaded files' },
        { name: 'codevaro_admins', schema: 'public', description: 'Stores admin users' }
      ]
      
      setServerInfo({
        supabaseUrl: serverSettings?.settings?.supabaseUrl || import.meta.env.VITE_SUPABASE_URL || '',
        anonKey: serverSettings?.settings?.anonKey || import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        serviceRoleKey: serverSettings?.settings?.serviceRoleKey || '',
        recaptchaSiteKey: serverSettings?.settings?.recaptchaSiteKey || '',
        recaptchaSecretKey: serverSettings?.settings?.recaptchaSecretKey || '',
        tables: tablesData || manualTables,
        policies: policiesData || [],
        functions: functionsData || []
      })
    } catch (error) {
      console.error('Error fetching server info:', error)
      setError('Σφάλμα κατά τη φόρτωση των πληροφοριών του διακομιστή')
    } finally {
      setLoading(false)
    }
  }
  
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setServerInfo(prev => ({
      ...prev,
      [name]: value
    }))
  }
  
  const saveServerSettings = async () => {
    try {
      setError(null)
      setSuccess(null)
      
      const { data, error } = await supabase
        .from('codevaro_server_settings')
        .upsert({
          id: 'server_info',
          settings: {
            supabaseUrl: serverInfo.supabaseUrl,
            anonKey: serverInfo.anonKey,
            serviceRoleKey: serverInfo.serviceRoleKey,
            recaptchaSiteKey: serverInfo.recaptchaSiteKey,
            recaptchaSecretKey: serverInfo.recaptchaSecretKey
          },
          updated_at: new Date()
        })
        .select()
      
      if (error) throw error
      
      setSuccess('Οι ρυθμίσεις του διακομιστή αποθηκεύτηκαν επιτυχώς')
      setTimeout(() => setSuccess(null), 5000)
    } catch (error) {
      console.error('Error saving server settings:', error)
      setError('Σφάλμα κατά την αποθήκευση των ρυθμίσεων του διακομιστή')
    }
  }
  
  const createRpcFunctions = async () => {
    try {
      setError(null)
      setSuccess(null)
      
      // Use try-catch instead of .catch for better error handling
      try {
        const { error } = await supabase.rpc('create_info_functions')
        if (error) throw error
      } catch (err) {
        throw new Error('Error creating RPC functions: ' + (err.message || 'Unknown error'))
      }
      
      setSuccess('Οι συναρτήσεις RPC δημιουργήθηκαν επιτυχώς')
      setTimeout(() => {
        setSuccess(null)
        fetchServerInfo()
      }, 2000)
    } catch (error) {
      console.error('Error creating RPC functions:', error)
      setError('Σφάλμα κατά τη δημιουργία των συναρτήσεων RPC')
    }
  }
  
  const renderTabContent = () => {
    switch(activeTab) {
      case 'tables':
        return (
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Όνομα
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Schema
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Περιγραφή
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serverInfo.tables.map((table, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {table.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {table.schema}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {table.description || '-'}
                      </td>
                    </tr>
                  ))}
                  {serverInfo.tables.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                        Δεν βρέθηκαν πίνακες
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      
      case 'policies':
        return (
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Όνομα
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Πίνακας
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Λειτουργία
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serverInfo.policies.map((policy, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {policy.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {policy.table_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {policy.action}
                      </td>
                    </tr>
                  ))}
                  {serverInfo.policies.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                        Δεν βρέθηκαν πολιτικές ασφαλείας
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      
      case 'functions':
        return (
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Όνομα
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Schema
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Τύπος
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serverInfo.functions.map((func, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {func.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {func.schema}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {func.type}
                      </td>
                    </tr>
                  ))}
                  {serverInfo.functions.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                        Δεν βρέθηκαν συναρτήσεις
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      
      case 'keys':
        return (
          <div className="mt-4">
            <div className="space-y-4">
              <div>
                <label htmlFor="supabaseUrl" className="block text-sm font-medium text-gray-700">
                  Supabase URL
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="supabaseUrl"
                    id="supabaseUrl"
                    value={serverInfo.supabaseUrl}
                    onChange={handleInputChange}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="anonKey" className="block text-sm font-medium text-gray-700">
                  Anon Key
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type={showKeys ? "text" : "password"}
                    name="anonKey"
                    id="anonKey"
                    value={serverInfo.anonKey}
                    onChange={handleInputChange}
                    className="focus:ring-blue-500 focus:border-blue-500 flex-1 block w-full rounded-l-md sm:text-sm border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeys(!showKeys)}
                    className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                  >
                    <SafeIcon icon={showKeys ? FiIcons.FiEyeOff : FiIcons.FiEye} />
                  </button>
                </div>
              </div>
              
              <div>
                <label htmlFor="serviceRoleKey" className="block text-sm font-medium text-gray-700">
                  Service Role Key
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type={showKeys ? "text" : "password"}
                    name="serviceRoleKey"
                    id="serviceRoleKey"
                    value={serverInfo.serviceRoleKey}
                    onChange={handleInputChange}
                    className="focus:ring-blue-500 focus:border-blue-500 flex-1 block w-full rounded-l-md sm:text-sm border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeys(!showKeys)}
                    className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
                  >
                    <SafeIcon icon={showKeys ? FiIcons.FiEyeOff : FiIcons.FiEye} />
                  </button>
                </div>
              </div>
              
              <div>
                <label htmlFor="recaptchaSiteKey" className="block text-sm font-medium text-gray-700">
                  reCAPTCHA Site Key
                </label>
                <div className="mt-1">
                  <input
                    type={showKeys ? "text" : "password"}
                    name="recaptchaSiteKey"
                    id="recaptchaSiteKey"
                    value={serverInfo.recaptchaSiteKey}
                    onChange={handleInputChange}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="recaptchaSecretKey" className="block text-sm font-medium text-gray-700">
                  reCAPTCHA Secret Key
                </label>
                <div className="mt-1">
                  <input
                    type={showKeys ? "text" : "password"}
                    name="recaptchaSecretKey"
                    id="recaptchaSecretKey"
                    value={serverInfo.recaptchaSecretKey}
                    onChange={handleInputChange}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
              
              <div className="pt-5">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={saveServerSettings}
                    className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <SafeIcon icon={FiIcons.FiSave} className="mr-2" />
                    Αποθήκευση
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      
      default:
        return null
    }
  }
  
  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Πληροφορίες Διακομιστή</h1>
      
      {error && (
        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{success}</span>
        </div>
      )}
      
      <div className="mt-6">
        {loading ? (
          <div className="flex justify-center">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-8 w-8 text-blue-500" />
          </div>
        ) : (
          <div>
            <div className="sm:hidden">
              <label htmlFor="tabs" className="sr-only">
                Επιλέξτε καρτέλα
              </label>
              <select
                id="tabs"
                name="tabs"
                className="block w-full focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
              >
                <option value="tables">Πίνακες</option>
                <option value="policies">Πολιτικές Ασφαλείας</option>
                <option value="functions">Συναρτήσεις</option>
                <option value="keys">Κλειδιά API</option>
              </select>
            </div>
            <div className="hidden sm:block">
              <nav className="flex space-x-4" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('tables')}
                  className={`${
                    activeTab === 'tables'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  } px-3 py-2 font-medium text-sm rounded-md`}
                >
                  Πίνακες
                </button>
                <button
                  onClick={() => setActiveTab('policies')}
                  className={`${
                    activeTab === 'policies'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  } px-3 py-2 font-medium text-sm rounded-md`}
                >
                  Πολιτικές Ασφαλείας
                </button>
                <button
                  onClick={() => setActiveTab('functions')}
                  className={`${
                    activeTab === 'functions'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  } px-3 py-2 font-medium text-sm rounded-md`}
                >
                  Συναρτήσεις
                </button>
                <button
                  onClick={() => setActiveTab('keys')}
                  className={`${
                    activeTab === 'keys'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  } px-3 py-2 font-medium text-sm rounded-md`}
                >
                  Κλειδιά API
                </button>
              </nav>
            </div>
            
            {renderTabContent()}
            
            {(activeTab === 'tables' || activeTab === 'policies' || activeTab === 'functions') && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={createRpcFunctions}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <SafeIcon icon={FiIcons.FiRefreshCw} className="mr-2" />
                  Ενημέρωση πληροφοριών
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ServerInfo